import React, { useState } from "react";
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { MinusIcon, PlusIcon, CopySimpleIcon } from "@phosphor-icons/react";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { timeToDate } from "utils/dateTimeUtils";
import { CopyAvailabilityMenu } from "./CopyAvailabilityMenu";
import type {
  CopyToDays,
  UpdateDayData,
  WorkingHoursRange,
  WorkingHoursSelectorSharedProps,
} from "./WorkingHoursSelector";

type WorkingHoursSelectorEditProps = WorkingHoursSelectorSharedProps & {
  defaultNewRange: WorkingHoursRange;
  updateDayData: UpdateDayData;
  copyToDays: CopyToDays;
};

const generateTimeOptions = () => {
  const times: string[] = [];
  const today = new Date();

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour, minute);
      times.push(format(date, "h:mm a"));
    }
  }

  return times;
};

/**
 * Owns the controls and mutations for one editable working-hours row.
 */
export const WorkingHoursSelectorEdit = ({
  day,
  isActive,
  timeRanges,
  defaultNewRange,
  updateDayData,
  copyToDays,
  clinicianTimeZone,
}: WorkingHoursSelectorEditProps) => {
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const dayLabel = `${day.charAt(0).toUpperCase() + day.slice(1)}`;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCopyMenuOpen(true);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setCopyMenuOpen(false);
    setMenuAnchorEl(null);
  };

  const formatTime = (dateString: string) =>
    formatInTimeZone(new Date(dateString), clinicianTimeZone, "h:mm a");

  const handleTimeChange = (index: number, field: "start" | "end", event: SelectChangeEvent) => {
    const updatedRanges = timeRanges.map((range, rangeIndex) => {
      if (rangeIndex !== index) {
        return range;
      }

      const dateObj = timeToDate(event.target.value, new Date(), clinicianTimeZone);
      const todayInClinicianTimeZone = formatInTimeZone(
        new Date(),
        clinicianTimeZone,
        "yyyy-MM-dd"
      );
      const newValue = formatInTimeZone(dateObj, clinicianTimeZone, "yyyy-MM-dd HH:mm");
      const finalValue = formatInTimeZone(
        timeToDate(newValue.split(" ")[1], todayInClinicianTimeZone, clinicianTimeZone),
        clinicianTimeZone,
        "yyyy-MM-dd'T'HH:mm:ssXXX"
      );
      const newRange = { ...range, [field]: finalValue };
      const startTimestamp = new Date(newRange.start).getTime();
      const endTimestamp = new Date(newRange.end).getTime();

      if (field === "start" && endTimestamp < startTimestamp) {
        newRange.end = newRange.start;
      } else if (field === "end" && startTimestamp > endTimestamp) {
        newRange.start = newRange.end;
      }

      return newRange;
    });

    updateDayData(day, isActive, updatedRanges);
  };

  const addTimeRange = (index: number) => {
    const updatedTimeRanges = [
      ...timeRanges.slice(0, index + 1),
      defaultNewRange,
      ...timeRanges.slice(index + 1),
    ];

    updateDayData(day, isActive, updatedTimeRanges);
  };

  const removeTimeRange = (index: number) => {
    const newTimeRanges = timeRanges.filter((_, rangeIndex) => rangeIndex !== index);
    updateDayData(day, isActive, newTimeRanges);
  };

  const timeOptions = generateTimeOptions();

  return (
    <Stack flexDirection="row" gap="16px" alignItems="flex-start">
      <FormControlLabel
        style={{ width: 128, height: 40 }}
        control={
          <Checkbox
            checked={isActive}
            onChange={() => {
              updateDayData(day, !isActive, !timeRanges.length ? [defaultNewRange] : timeRanges);
            }}
          />
        }
        label={dayLabel}
      />
      {isActive ? (
        <Stack gap="16px">
          {timeRanges.map((timeRange, index) => (
            <Stack flexDirection="row" key={index} alignItems="center" gap="16px">
              <Select
                value={formatTime(timeRange.start)}
                onChange={(event) => handleTimeChange(index, "start", event)}
                style={{ width: "128px" }}
              >
                {timeOptions.map((time) => (
                  <MenuItem key={time} value={time}>
                    {time}
                  </MenuItem>
                ))}
              </Select>

              <Typography variant="body.small">to</Typography>

              <Select
                value={formatTime(timeRange.end)}
                onChange={(event) => handleTimeChange(index, "end", event)}
                style={{ width: "128px" }}
              >
                {timeOptions.map((time) => (
                  <MenuItem key={time} value={time}>
                    {time}
                  </MenuItem>
                ))}
              </Select>

              {index > 0 ? (
                <>
                  <Tooltip title={`Remove interval for ${dayLabel}`}>
                    <IconButton
                      size="medium"
                      fontSize="small"
                      onClick={() => removeTimeRange(index)}
                    >
                      <MinusIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={`New interval for ${dayLabel}`}>
                    <IconButton size="medium" fontSize="small" onClick={() => addTimeRange(index)}>
                      <PlusIcon />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip title={`New interval for ${dayLabel}`}>
                    <IconButton size="medium" fontSize="small" onClick={() => addTimeRange(index)}>
                      <PlusIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={`Copy ${dayLabel}'s Availability`}>
                    <IconButton size="medium" fontSize="small" onClick={handleMenuOpen}>
                      <CopySimpleIcon />
                    </IconButton>
                  </Tooltip>
                  <CopyAvailabilityMenu
                    anchorEl={menuAnchorEl}
                    isMenuOpen={copyMenuOpen}
                    handleMenuClose={handleMenuClose}
                    copyToDays={copyToDays}
                    chosenDay={day}
                  />
                </>
              )}
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography
          variant="body.small"
          style={{ display: "flex", alignItems: "center", height: "40px" }}
        >
          Unavailable
        </Typography>
      )}
    </Stack>
  );
};
